/**
 * Per-page SEO metadata (spec section 18).
 *
 * Unique title and description for every indexable page, canonical URLs
 * relative to PUBLIC_SITE_URL, and Open Graph / Twitter tags sharing one social
 * image slot. Thank-you routes and the 404 are noindex, matching the route
 * table in spec section 05.
 *
 * Descriptions say what the page is. None of them claims the network is open,
 * counts anyone who has joined, or dates a launch.
 */

import { socialShareImagePath } from '../../shared/content/images';

export interface PageSeo {
  /** Path this metadata belongs to. Also the canonical path. */
  path: string;
  /** Page half of the title; the brand half comes from `titleTemplate`. */
  title: string;
  description: string;
  /** True for routes that must stay out of the index (spec section 05). */
  noindex?: boolean;
}

export const seoDefaults = {
  /** `{page}` and `{brand}` are substituted at render time. */
  titleTemplate: '{page} | {brand}',
  ogType: 'website',
  twitterCard: 'summary_large_image',
  locale: 'en_US',
  /**
   * Documented, reserved path for the share image. The file itself is
   * owner-supplied and is not in the repository — see the image slot manifest
   * and the README image slot table.
   */
  ogImagePath: socialShareImagePath,
  ogImageAlt: 'My Herd Book',
} as const;

export const pageSeo = {
  home: {
    path: '/',
    title: 'Find Maryland farriers, vets, trainers and haulers',
    description:
      'A Maryland network for finding the people who take care of your horse. Join early access as an owner or a Founding Provider.',
  },
  owners: {
    path: '/owners',
    title: 'Find the right people for your horse',
    description:
      'Farriers, vets, haulers, trainers, bodyworkers, barn builders and saddle fitters in your part of Maryland. Join early access.',
  },
  providers: {
    path: '/providers',
    title: 'Be the name that comes up',
    description:
      "Show what you do, how far you'll drive, and how to reach you. Join Maryland early access as a Founding Provider. No charge, no credit card.",
  },
  privacy: {
    path: '/privacy',
    title: 'Privacy',
    description: 'How My Herd Book handles the information collected during Maryland early access.',
  },
  terms: {
    path: '/terms',
    title: 'Terms',
    description: 'The terms that govern My Herd Book, including the Founding Provider program.',
  },
  thankYouOwner: {
    path: '/thank-you/owner',
    title: 'Thank you',
    description: 'Owner early access confirmation.',
    noindex: true,
  },
  thankYouProvider: {
    path: '/thank-you/provider',
    title: 'Thank you',
    description: 'Founding Provider confirmation.',
    noindex: true,
  },
  /**
   * The URL carries an unsubscribe token, so this page must never be indexed
   * and never appear in a sitemap (spec sections 17, 18).
   */
  unsubscribe: {
    path: '/unsubscribe',
    title: 'Email preferences',
    description: 'Stop receiving email from My Herd Book.',
    noindex: true,
  },
  notFound: {
    path: '/404',
    title: 'Page not found',
    description: 'That page does not exist on My Herd Book.',
    noindex: true,
  },
} satisfies Record<string, PageSeo>;

export type PageSeoKey = keyof typeof pageSeo;
